// @ts-nocheck
import React, {
  useState,
  useCallback,
  useMemo,
  useEffect,
  useRef,
} from 'react';
import {
  Box,
  Grid,
  List,
  ListItemButton,
  Typography,
  IconButton,
  ButtonBase,
  Tooltip,
  Card,
  Badge,
  Alert,
  Button,
  useTheme,
} from '@mui/material';
import PropTypes from 'prop-types';
import { ReactComponent as ModeEditOutlinedIcon } from '../../../../assets/Icons/lucideIcons/pencil.svg';
import { ReactComponent as ChatIcon } from '../../../../assets/Icons/lucideIcons/message-square-more.svg';
import { ReactComponent as InfoOutlinedIcon } from '../../../../assets/Icons/lucideIcons/info.svg';
import { useSelector } from 'react-redux';
import { useLocation } from 'react-router-dom';
import { ReactComponent as HistoryIcon } from '../../../../assets/Icons/lucideIcons/history.svg';

import useStateParams from '../../../../hooks/useStateParams';

import ReactMuiTableListView from '../../ReactMuiTableListViewV8';
import EditValueModal from '../Modals/EditValueModal';
import AssignUserModal from '../Modals/AssignUserModal';
import HistoryDrawer from '../Modals/HistoryDrawer';
import MultiEditAttributeField from '../MultiEditAttributeField';
import AttributeInfoPopover from './AttributeInfoPopover';
import {
  formatAttributeValue,
  isValueEmpty,
  isRichContentEmpty,
  findGroupNameByGroupId,
  findUsernameByUserId,
  checkAttributeIsRequired,
} from '../../../../utils/Utils';
import { getVisibleAttributes } from '../../../../utils/conditionalRuleEvaluator';
import { extractAllAttributesFromTemplate } from '../../../../features/configuration/template/components/SectionAndAttribute/commonFunction';
import {
  HDFC_HARDCODED_DEPLOYMENT_WORKFLOW_STATES,
  validateDeploymentDate,
  validateDeploymentDateCommunication,
} from '../../../../utils/deploymentDateUtils';
import useTextOverflow from '../../../../hooks/useTextOverFlow';
import NoDataPresentComponent from '../../NoDataPresentComponent';
import { ReactComponent as CalculationIcon } from '../../../../assets/images/CalculationIcon.svg';
import DerivedAttributeOverrideModal from '../DerivedAttributeOverrideModal';
import TextEditor from '../../ModelInventoryAndAssociation/TextEditor/TextEditor';
import { areSetsEqual } from '../BasicDetails/BasicDetails';
import { createInitialFormValues } from '../../../../utils/formUtils';
import { EMPTY_ARRAY, EMPTY_OBJECT, NOOP } from '../../../../const/CommonConst';
import {
  useShowDraftChip,
  useDraftAttributeIds,
  attributeShowsDraftInput,
} from '../../../../hooks/useDraftAutoSave.jsx';
import {
  DraftAttributeChip,
  DraftDot,
} from '../FieldComponents/DraftFieldExtras';
import { ValueCellAttributeValueDisplay } from './ValueCell';
import {
  useGroupsQuery,
  useUsersQuery,
} from '../../../../features/administrativeOptions/userManagement/api/UserManagementQueries';

const USER_DATA_TYPE = 'User';
const GROUP_DATA_TYPE = 'Group';
const MULTI_SELECT_INPUT_CONTROL = 'MULTI_SELECT';
const DRAFT_ROW_CELL_PADDING = '8px';
const STANDARD_ROW_HEIGHT = '50px';

const withDraftRowFlag = (attributes, draftAttributeIds) =>
  attributes.map((attr) => ({
    ...attr,
    isDraftRow:
      draftAttributeIds?.some((id) => String(id) === String(attr?.id)) &&
      attributeShowsDraftInput(attr),
  }));

/**
 * Custom ListItemText component for section names with word wrapping
 * @param {Object} props - Component props
 * @param {string} props.text - The section name text
 * @returns {React.ReactElement} The custom list item text component
 */
const SectionListItemText = ({
  text,
  sectionId,
  hasMandatoryAttribute = false,
  attributes = EMPTY_ARRAY,
}) => {
  const { ref: textRef } = useTextOverflow(text);
  const mandatoryCount = useMemo(
    () =>
      attributes?.filter((attribute) => checkAttributeIsRequired(attribute))
        ?.length || 0,
    [attributes]
  );

  return (
    <Box sx={{ width: '100%', display: 'flex', alignItems: 'center' }}>
      <DraftDot
        attributes={attributes}
        sectionId={sectionId}
        sectionName={text}
      />
      <Box
        sx={{
          minWidth: 0,
          flex: 1,
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
          minHeight: 36,
        }}
      >
        <Typography
          ref={textRef}
          variant="body6"
          sx={{
            display: 'block',
            whiteSpace: 'normal',
            wordBreak: 'break-word',
            width: '100%',
          }}
        >
          {text || ''}
          {(hasMandatoryAttribute || mandatoryCount > 0) && (
            <Typography
              component="span"
              variant="body8"
              aria-label="Section has mandatory fields"
              sx={{
                color: 'error.main',
                marginLeft: '2px',
                flexShrink: 0,
                lineHeight: 1,
              }}
            >
              *
            </Typography>
          )}
        </Typography>
        {mandatoryCount > 0 && (
          <Typography
            variant="tiny"
            sx={{
              color: 'text.disabled',
            }}
          >
            {`(${mandatoryCount} Mandatory ${
              mandatoryCount === 1 ? 'Attribute' : 'Attributes'
            })`}
          </Typography>
        )}
      </Box>
    </Box>
  );
};

SectionListItemText.propTypes = {
  text: PropTypes.string,
  sectionId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  hasMandatoryAttribute: PropTypes.bool,
  attributes: PropTypes.arrayOf(PropTypes.shape({})),
};

/**
 * InfoIconButton component for displaying attribute information
 * @param {Object} props - Component props
 * @param {Object} props.attribute - The attribute data
 * @param {Object} props.attributeSourceMapping - Mapping of attribute sources
 * @param {boolean} props.loading - Whether data is loading
 * @returns {React.ReactElement} The InfoIconButton component
 */
const InfoIconButton = ({ attribute, attributeSourceMapping, loading }) => {
  const [infoAnchorEl, setInfoAnchorEl] = useState(null);
  const isInfoPopoverOpen = Boolean(infoAnchorEl);

  // Handle info popover open
  const handleInfoClick = useCallback(
    (event) => {
      event.stopPropagation();
      setInfoAnchorEl(event.currentTarget);
    },
    [setInfoAnchorEl]
  );

  // Handle info popover close
  const handleInfoClose = useCallback(() => {
    setInfoAnchorEl(null);
  }, [setInfoAnchorEl]);

  return (
    <>
      <IconButton
        color="primary"
        title="Info"
        onClick={handleInfoClick}
        size="small"
      >
        <InfoOutlinedIcon style={{ width: 18, height: 18 }} />
      </IconButton>
      {isInfoPopoverOpen && (
        <AttributeInfoPopover
          open={isInfoPopoverOpen}
          anchorEl={infoAnchorEl}
          onClose={handleInfoClose}
          attribute={attribute}
          attributeSourceMapping={attributeSourceMapping}
          loading={loading}
        />
      )}
    </>
  );
};

InfoIconButton.propTypes = {
  attribute: PropTypes.shape({
    id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    data_type: PropTypes.string,
    attribute_origin: PropTypes.string,
  }),
  attributeSourceMapping: PropTypes.objectOf(
    PropTypes.oneOfType([
      PropTypes.string,
      PropTypes.number,
      PropTypes.bool,
      PropTypes.array,
      PropTypes.object,
    ])
  ),
  loading: PropTypes.bool,
};

const handleGetHeaderProps = () => ({
  style: {
    display: 'flex',
    alignItems: 'center',
  },
});

/**
 * Cell component for value column
 * @param {Object} props - Component props
 * @param {Object} props.row - The row data
 * @param {any} props.value - The cell value
 * @param {Function} props.onEdit - Function to handle edit click
 * @param {boolean} props.editPermission - Whether user can edit
 * @param {boolean} props.isMultipleEditMode - Whether in multiple edit mode
 * @param {Object} props.entityDetailsFormikForm - Formik form object
 * @param {Array} props.usersList - List of users
 * @param {Array} props.teamUsersList - List of team users
 * @param {Object} props.entityData - Entity data
 * @param {Function} props.handleChange - Stable change handler function
 * @param {Function} props.handleFocus - Stable focus handler function
 * @param {Function} props.handleBlur - Stable blur handler function
 * @param {Object} props.attributeSourceMapping - Mapping of attribute sources
 * @param {boolean} props.loading - Whether data is loading
 * @param {boolean} props.isTableView - Whether displayed in table view
 * @returns {React.ReactElement} The cell component
 */
const ValueCell = ({
  row,
  value,
  onEdit,
  editPermission,
  isMultipleEditMode,
  entityDetailsFormikForm,
  usersList,
  teamUsersList,
  entityData,
  handleChange,
  attributeSourceMapping,
  loading,
  isTableView = true,
  isDerived,
  overrideData,
  handleOpenDerivedModal,
  handleAutoSave,
  // Constraint-related props
  lookupSourceAttributes,
  autoCompleteSourceAttributes,
  overrideDropdownOptions,
  isLoadingConstraints,
  dateCalendarConstraints,
  dateCalendarSourceAttributes,
  editableConstraints,
  editableSourceAttributes,
  mandatoryConstraints = {},
  mandatorySourceAttributes = [],
  hasDraftChanges = false,
  groupDetails = {},
  attributeValues,
  savedAttributeValues = EMPTY_OBJECT,
  associationTypes = EMPTY_OBJECT,
  // AI Autofill props
  getSuggestionForAttribute,
  onAcceptAISuggestion,
  onRejectAISuggestion,
  onUndoAISuggestion,
  onHistoryClick = NOOP,
  modelTemplateDetails = EMPTY_OBJECT,
  isUserAuthorizedToOverrideAttribute,
}) => {
  const { data: groupList = EMPTY_ARRAY } = useGroupsQuery();

  const attribute = {
    ...(row?.original || {}),
    entityType: entityData?.entity_type,
    requestType: entityData?.request_type,
    ...(editableConstraints?.[row?.original?.id] !== undefined && {
      is_editable:
        editableConstraints[row?.original?.id]?.is_editable !== false,
    }),
    // Apply mandatory constraints if present for this attribute
    ...(mandatoryConstraints?.[row?.original?.id] !== undefined && {
      is_mandatory:
        mandatoryConstraints[row?.original?.id]?.is_mandatory === true,
    }),
  };
  const { frontendConfig, pluginConfig } = useSelector(
    (state) => state?.tenant
  );
  const { permissions } = useSelector((state) => state?.users || {});
  const showDraftChip = useShowDraftChip(attribute);
  const fieldAlignItems =
    isTableView && showDraftChip ? 'flex-start' : 'center';

  const isEditEnabled = useMemo(() => {
    if (
      attribute?.name?.toLowerCase() === 'assigneduser' &&
      pluginConfig?.HDFCUAM
    ) {
      return permissions?.is_super_admin || groupDetails?.isGroupAdmin;
    }
    return (
      !isMultipleEditMode &&
      !isDerived &&
      editPermission &&
      !frontendConfig?.hideSingleEditMode
    );
  }, [
    isMultipleEditMode,
    isDerived,
    editPermission,
    frontendConfig?.hideSingleEditMode,
    attribute?.name,
    permissions?.is_super_admin,
    groupDetails?.isGroupAdmin,
    pluginConfig?.HDFCUAM,
  ]);

  // Create a bound change handler for this specific attribute (always created, not conditionally)
  const attributeChangeHandler = useCallback(
    (newValue) => {
      if (attribute?.id) {
        handleChange(attribute.id, newValue);
      }
    },
    [handleChange, attribute?.id]
  );
  const handleClickAttribute = useCallback(
    (e) => {
      e.stopPropagation();
      handleOpenDerivedModal(attribute);
    },
    [handleOpenDerivedModal, attribute]
  );
  const matchedOverride = useMemo(() => {
    return overrideData?.find?.((item) => item?.attributeId === attribute?.id);
  }, [overrideData, attribute?.id]);

  // In multiple edit mode, use formik values; otherwise use the passed value
  // Get the raw value (IDs for User/Group) to be passed to the input component
  const rawValue = useMemo(() => {
    let currentValue;
    if (isMultipleEditMode) {
      const hasFormikValue = Object.hasOwn(
        entityDetailsFormikForm?.values ?? {},
        attribute?.id
      );
      currentValue = hasFormikValue
        ? entityDetailsFormikForm?.values[attribute?.id]
        : value;
    } else {
      currentValue = value;
    }
    return currentValue;
  }, [
    isMultipleEditMode,
    entityDetailsFormikForm?.values,
    attribute?.id,
    value,
  ]);

  // In multiple edit mode, use formik values; otherwise use the passed value
  const displayValue = useMemo(() => {
    let currentValue = rawValue;

    if (
      attribute?.data_type === GROUP_DATA_TYPE &&
      attribute?.input_control_type === MULTI_SELECT_INPUT_CONTROL
    ) {
      const groupIds = currentValue || [];
      const groupNames = groupIds?.map((groupId) =>
        findGroupNameByGroupId(groupId, groupList)
      );
      currentValue = groupNames?.join(', ');
    }

    if (
      attribute?.data_type === USER_DATA_TYPE &&
      attribute?.input_control_type === MULTI_SELECT_INPUT_CONTROL
    ) {
      const userIds = currentValue || [];
      // for multiselect user attribute, if the value is not an array of ids return empty array
      if (!Array.isArray(userIds) || userIds.length === 0) {
        return [];
      }
      const userNames = userIds?.map((userId) =>
        findUsernameByUserId(userId, usersList)
      );
      currentValue = userNames?.join(', ');
    }

    return currentValue;
  }, [
    attribute?.data_type,
    attribute?.input_control_type,
    groupList,
    usersList,
    rawValue,
  ]);

  // Use the centralized utility function to format the value for display
  const formattedDisplayValue = formatAttributeValue(
    displayValue,
    attribute,
    hasDraftChanges,
    entityData?.entity_id,
    entityDetailsFormikForm,
    usersList,
    groupList,
    entityData?.entity_type,
    associationTypes,
    false,
    false,
    entityData
  );
  const { ref: textRef, isOverflowed } = useTextOverflow(formattedDisplayValue);

  const isEditable = useMemo(() => attribute?.is_editable, [attribute]);

  const showCalculationIcon = useMemo(() => {
    return (
      !isMultipleEditMode &&
      isDerived &&
      editPermission &&
      attribute?.input_control_type !== 'SINGLE_SELECT' &&
      !frontendConfig?.hideSingleEditMode
    );
  }, [
    isMultipleEditMode,
    isDerived,
    editPermission,
    attribute?.input_control_type,
    frontendConfig?.hideSingleEditMode,
  ]);

  //shows history icon
  const showHistoryIcon = useMemo(() => {
    return attribute?.show_history;
  }, [attribute]);

  const handleHistoryClick = useCallback(
    (e) => {
      e.stopPropagation();
      onHistoryClick(attribute);
    },
    [onHistoryClick, attribute]
  );

  //if attribute is non-editable
  if (!isEditable && !isDerived) {
    return (
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          width: '100%',
          '&:hover .edit-icon-button': {
            opacity: 1,
            visibility: 'visible',
          },
        }}
      >
        <Tooltip
          title={
            typeof formattedDisplayValue !== 'object' && isOverflowed
              ? formattedDisplayValue
              : ''
          }
        >
          <ValueCellAttributeValueDisplay
            formattedDisplayValue={formattedDisplayValue}
            textRef={textRef}
            matchedOverride={matchedOverride}
            includeOverrideTooltip={false}
          />
        </Tooltip>
        <InfoIconButton
          attribute={attribute}
          attributeSourceMapping={attributeSourceMapping}
          loading={loading}
        />
      </Box>
    );
  }

  // If in multiple edit mode and user has edit permission, show the form field
  if (isMultipleEditMode && editPermission) {
    return (
      <Box
        sx={{
          width: '100%',
          minHeight: '40px',
          display: 'flex',
          alignItems: fieldAlignItems,
          // This is for removing the label of the field
          '& .MuiFormControl-root': {
            marginTop: '0px',
            '& .MuiInputLabel-root': {
              display: 'none',
            },
          },
        }}
      >
        <Box
          sx={{
            width: '100%',
            display: 'flex',
            alignItems: fieldAlignItems,

            ...(!isTableView && {
              justifyContent: 'space-between',
            }),
          }}
        >
          <Box
            sx={{
              flex: 1,
              minWidth: 0,

              ...(!isTableView && {
                maxWidth: '350px',
              }),
            }}
          >
            <MultiEditAttributeField
              attribute={attribute}
              value={rawValue}
              usersList={usersList}
              teamUsersList={teamUsersList}
              entityData={entityData}
              onChange={attributeChangeHandler}
              showLabel={false}
              entityDetailsFormikForm={entityDetailsFormikForm}
              handleOpenDerivedModal={handleOpenDerivedModal}
              isDerived={isDerived}
              handleAutoSave={handleAutoSave}
              onHistoryClick={onHistoryClick || handleHistoryClick}
              // Constraint-related props
              lookupSourceAttributes={lookupSourceAttributes}
              autoCompleteSourceAttributes={autoCompleteSourceAttributes}
              overrideDropdownOptions={overrideDropdownOptions}
              isLoadingConstraints={isLoadingConstraints}
              dateCalendarConstraints={dateCalendarConstraints}
              dateCalendarSourceAttributes={dateCalendarSourceAttributes}
              editableSourceAttributes={editableSourceAttributes}
              mandatorySourceAttributes={mandatorySourceAttributes}
              groupDetails={groupDetails}
              isUserAuthorizedForUpdateAttribute={editPermission}
              isMultipleEditMode={isMultipleEditMode}
              attributeValues={attributeValues}
              savedAttributeValues={savedAttributeValues}
              descriptiveDraftFooter
              // AI Autofill
              aiSuggestion={getSuggestionForAttribute?.(attribute?.id)}
              onAcceptAISuggestion={onAcceptAISuggestion}
              onRejectAISuggestion={onRejectAISuggestion}
              onUndoAISuggestion={onUndoAISuggestion}
              modelTemplateDetails={modelTemplateDetails}
              isUserAuthorizedToOverrideAttribute={
                isUserAuthorizedToOverrideAttribute
              }
            />
          </Box>
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'row',
              alignItems: 'center',
              gap: 1,
            }}
          >
            {showHistoryIcon && (
              <Tooltip title="History">
                <ButtonBase
                  onClick={handleHistoryClick}
                  aria-label={`View history for ${attribute?.label || attribute?.name}`}
                  sx={{ color: 'primary.main', flexShrink: 0 }}
                >
                  <HistoryIcon width={16} height={16} />
                </ButtonBase>
              </Tooltip>
            )}
            <Box sx={{ flexShrink: 0 }}>
              <InfoIconButton
                attribute={attribute}
                attributeSourceMapping={attributeSourceMapping}
                loading={loading}
              />
            </Box>
          </Box>
        </Box>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        width: '100%',
        '&:hover .edit-icon-button': {
          opacity: 1,
          visibility: 'visible',
        },
      }}
    >
      <Tooltip
        title={
          typeof formattedDisplayValue !== 'object' && isOverflowed
            ? formattedDisplayValue
            : ''
        }
      >
        <ValueCellAttributeValueDisplay
          formattedDisplayValue={formattedDisplayValue}
          textRef={textRef}
          matchedOverride={matchedOverride}
          includeOverrideTooltip
        />
      </Tooltip>
      {/* Show ONLY CalculationIcon if derived */}
      {showCalculationIcon && (
        <IconButton
          className="edit-icon-button disabled-svg-icon"
          onClick={handleClickAttribute}
          color="primary"
          title={
            !attribute?.show_calculation_popUp
              ? 'Enable the calculation popup to calculate this attribute.'
              : `Calculate ${attribute?.label}`
          }
          disabled={!attribute?.show_calculation_popUp}
          sx={{
            flexShrink: 0,
            opacity: 0,
            visibility: 'hidden',
            transition: 'opacity 0.2s ease-in-out, visibility 0.2s ease-in-out',
          }}
        >
          <CalculationIcon width={18} height={18} />
        </IconButton>
      )}

      {/* Show edit icon only if NOT derived */}
      {isEditEnabled && (
        <IconButton
          className="edit-icon-button"
          onClick={onEdit}
          size="small"
          color="primary"
          sx={{
            flexShrink: 0,
            opacity: 0,
            visibility: 'hidden',
            transition: 'opacity 0.2s ease-in-out, visibility 0.2s ease-in-out',
          }}
        >
          <ModeEditOutlinedIcon width={18} height={18} />
        </IconButton>
      )}

      {showHistoryIcon && (
        <Tooltip title="History">
          <ButtonBase
            onClick={handleHistoryClick}
            aria-label={`View history for ${attribute?.label || attribute?.name}`}
            sx={{ color: 'primary.main', flexShrink: 0 }}
          >
            <HistoryIcon width={16} height={16} />
          </ButtonBase>
        </Tooltip>
      )}

      <InfoIconButton
        attribute={attribute}
        attributeSourceMapping={attributeSourceMapping}
        loading={loading}
      />
    </Box>
  );
};

ValueCell.propTypes = {
  row: PropTypes.shape({
    original: PropTypes.shape({
      id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
      data_type: PropTypes.string,
      input_control_type: PropTypes.string,
    }),
  }),
  value: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.number,
    PropTypes.bool,
    PropTypes.array,
    PropTypes.object,
  ]),
  hasDraftChanges: PropTypes.bool,
  associationTypes: PropTypes.objectOf(
    PropTypes.arrayOf(
      PropTypes.shape({
        entity_name: PropTypes.string,
        entity_type: PropTypes.string,
        template_id: PropTypes.string,
        version_id: PropTypes.string,
      })
    )
  ),
  groupDetails: PropTypes.shape({
    isGroupAdmin: PropTypes.bool,
    userName: PropTypes.string,
    userId: PropTypes.number,
    users: PropTypes.arrayOf(
      PropTypes.shape({
        userId: PropTypes.number,
        username: PropTypes.string,
      })
    ),
  }),
  onEdit: PropTypes.func.isRequired,
  editPermission: PropTypes.bool.isRequired,
  isMultipleEditMode: PropTypes.bool,
  entityDetailsFormikForm: PropTypes.shape({
    values: PropTypes.objectOf(
      PropTypes.oneOfType([
        PropTypes.string,
        PropTypes.number,
        PropTypes.bool,
        PropTypes.array,
        PropTypes.object,
      ])
    ),
    setFieldValue: PropTypes.func,
  }),
  usersList: PropTypes.arrayOf(PropTypes.shape({})),
  teamUsersList: PropTypes.arrayOf(PropTypes.shape({})),
  entityData: PropTypes.shape({
    entity_id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    entity_type: PropTypes.string,
    request_type: PropTypes.string,
  }),
  handleChange: PropTypes.func.isRequired,
  attributeSourceMapping: PropTypes.objectOf(
    PropTypes.oneOfType([
      PropTypes.string,
      PropTypes.number,
      PropTypes.bool,
      PropTypes.array,
      PropTypes.object,
    ])
  ),
  loading: PropTypes.bool,
  isTableView: PropTypes.bool,
  handleOpenDerivedModal: PropTypes.func.isRequired,
  isDerived: PropTypes.bool.isRequired,
  overrideData: PropTypes.oneOf([PropTypes.object]).isRequired,
  handleAutoSave: PropTypes.func,
  // Constraint-related props
  lookupSourceAttributes: PropTypes.arrayOf(PropTypes.shape({})),
  autoCompleteSourceAttributes: PropTypes.arrayOf(PropTypes.shape({})),
  overrideDropdownOptions: PropTypes.arrayOf(
    PropTypes.oneOfType([PropTypes.string, PropTypes.object])
  ),
  isLoadingConstraints: PropTypes.bool,
  dateCalendarConstraints: PropTypes.shape({}),
  dateCalendarSourceAttributes: PropTypes.arrayOf(PropTypes.shape({})),
  editableConstraints: PropTypes.shape({}),
  editableSourceAttributes: PropTypes.arrayOf(PropTypes.shape({})),
  mandatoryConstraints: PropTypes.shape({}),
  mandatorySourceAttributes: PropTypes.arrayOf(PropTypes.number),
  attributeValues: PropTypes.shape({}),
  savedAttributeValues: PropTypes.shape({}),
  // AI Autofill props
  getSuggestionForAttribute: PropTypes.func,
  onAcceptAISuggestion: PropTypes.func,
  onRejectAISuggestion: PropTypes.func,
  onUndoAISuggestion: PropTypes.func,
  onHistoryClick: PropTypes.func,
  modelTemplateDetails: PropTypes.shape({}),
  isUserAuthorizedToOverrideAttribute: PropTypes.bool.isRequired,
};

/**
 * Cell renderer for the Value column
 * @param {Object} props - Cell props from React Table
 * @param {Object} props.row - The row data
 * @param {any} props.value - The cell value
 * @param {Function} handleEditClick - Function to handle edit click
 * @param {boolean} editPermission - Whether user can edit
 * @param {boolean} isMultipleEditMode - Whether in multiple edit mode
 * @param {Object} entityDetailsFormikForm - Formik form object
 * @param {Array} usersList - List of users
 * @param {Array} teamUsersList - List of team users
 * @param {Object} entityData - Entity data
 * @param {Function} handleChange - Stable change handler function
 * @param {Object} attributeSourceMapping - Mapping of attribute sources
 * @param {boolean} loading - Whether data is loading
 * @returns {React.ReactElement} The rendered cell
 */
const ValueCellRenderer = (
  props,
  handleEditClick,
  editPermission,
  isMultipleEditMode,
  entityDetailsFormikForm,
  usersList,
  teamUsersList,
  entityData,
  handleChange,
  attributeSourceMapping,
  loading,
  isTableView = true,
  isDerived,
  isUserAuthorizedToOverrideAttribute,
  overrideData,
  handleOpenDerivedModal,
  handleAutoSave,
  // Constraint-related props
  lookupSourceAttributes,
  autoCompleteSourceAttributes,
  overrideDropdownOptions,
  isLoadingConstraints,
  dateCalendarConstraints,
  dateCalendarSourceAttributes,
  editableConstraints,
  editableSourceAttributes,
  mandatoryConstraints,
  mandatorySourceAttributes,
  hasDraftChanges,
  groupDetails,
  attributeValues,
  savedAttributeValues,
  associationTypes,
  // AI Autofill props
  getSuggestionForAttribute,
  onAcceptAISuggestion,
  onRejectAISuggestion,
  onUndoAISuggestion,
  onHistoryClick,
  modelTemplateDetails
) => {
  const { row, value } = props;

  const onEdit = useCallback(() => {
    handleEditClick(row.original);
  }, [handleEditClick, row.original]);

  return (
    <ValueCell
      row={row}
      value={value}
      onEdit={onEdit}
      editPermission={editPermission}
      isMultipleEditMode={isMultipleEditMode}
      hasDraftChanges={hasDraftChanges}
      entityDetailsFormikForm={entityDetailsFormikForm}
      usersList={usersList}
      teamUsersList={teamUsersList}
      entityData={entityData}
      handleChange={handleChange}
      attributeSourceMapping={attributeSourceMapping}
      loading={loading}
      isTableView={isTableView}
      isDerived={isDerived}
      isUserAuthorizedToOverrideAttribute={isUserAuthorizedToOverrideAttribute}
      overrideData={overrideData}
      handleOpenDerivedModal={handleOpenDerivedModal}
      handleAutoSave={handleAutoSave}
      // Constraint-related props
      lookupSourceAttributes={lookupSourceAttributes}
      autoCompleteSourceAttributes={autoCompleteSourceAttributes}
      overrideDropdownOptions={overrideDropdownOptions}
      isLoadingConstraints={isLoadingConstraints}
      dateCalendarConstraints={dateCalendarConstraints}
      groupDetails={groupDetails}
      dateCalendarSourceAttributes={dateCalendarSourceAttributes}
      editableConstraints={editableConstraints}
      editableSourceAttributes={editableSourceAttributes}
      mandatoryConstraints={mandatoryConstraints}
      mandatorySourceAttributes={mandatorySourceAttributes}
      attributeValues={attributeValues}
      savedAttributeValues={savedAttributeValues}
      descriptiveDraftFooter
      associationTypes={associationTypes}
      // AI Autofill props
      getSuggestionForAttribute={getSuggestionForAttribute}
      onAcceptAISuggestion={onAcceptAISuggestion}
      onRejectAISuggestion={onRejectAISuggestion}
      onUndoAISuggestion={onUndoAISuggestion}
      onHistoryClick={onHistoryClick}
      modelTemplateDetails={modelTemplateDetails}
    />
  );
};

ValueCellRenderer.propTypes = {
  row: PropTypes.shape({
    original: PropTypes.shape({
      id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
      data_type: PropTypes.string,
      input_control_type: PropTypes.string,
    }),
  }),
  value: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.number,
    PropTypes.bool,
    PropTypes.array,
    PropTypes.object,
  ]),
};

/**
 * Cell component for name column
 * @param {Object} props - Component props
 * @param {any} props.value - The cell value (name/label)
 * @returns {React.ReactElement} The cell component
 */
const NameCell = ({
  value,
  attribute,
  allAttributeChats,
  handleOpenChat,
  handleAttributeClick,
  isTableView,
}) => {
  const showDraftChip = useShowDraftChip(attribute);
  const { ref: textRef, isOverflowed } = useTextOverflow(value);

  const openChatBox = useCallback(() => {
    handleOpenChat(allAttributeChats[attribute?.name], attribute);
  }, [allAttributeChats, attribute?.name, handleOpenChat]);

  // Check if field is required based on yup_schema
  const isRequired = useMemo(() => {
    return checkAttributeIsRequired(attribute);
  }, [attribute]);

  const handleClick = useCallback(() => {
    handleAttributeClick(attribute);
  }, [attribute, handleAttributeClick]);

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'row',
        paddingX: isTableView ? '10px' : '0px',
        gap: 1,
        width: '100%',
        ...(isTableView && showDraftChip
          ? { alignItems: 'flex-start' }
          : undefined),
      }}
    >
      <Box
        sx={{
          display: 'inline-flex',
          alignItems: isTableView && showDraftChip ? 'flex-start' : 'center',
          maxWidth: '100%',
          minWidth: 0,
        }}
      >
        <Tooltip title={typeof value !== 'object' && isOverflowed ? value : ''}>
          <Typography
            ref={textRef}
            variant={isTableView ? 'body2' : 'body4'}
            sx={{
              whiteSpace: 'normal',
              wordBreak: 'break-word',
              minWidth: 0,
              maxWidth: '100%',
              ':hover': {
                fontWeight: 600,
                cursor: 'pointer',
              },
            }}
            onClick={handleClick}
          >
            {value || ''}
            {/* Label wraps, so keep the asterisk and draft chip inline to sit
                right after the last word instead of at the box edge. */}
            {isRequired && (
              <Typography
                component="span"
                variant="body8"
                sx={{ color: 'error.main', marginLeft: '2px' }}
              >
                *
              </Typography>
            )}
            {showDraftChip && (
              <Box
                component="span"
                sx={{ display: 'inline-block', verticalAlign: 'middle' }}
              >
                <DraftAttributeChip withTooltip={false} />
              </Box>
            )}
          </Typography>
        </Tooltip>
      </Box>

      {!isValueEmpty(allAttributeChats[attribute?.name]) && (
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            cursor: 'pointer',
          }}
          onClick={openChatBox}
        >
          <Badge
            badgeContent={allAttributeChats[attribute?.name]?.length}
            sx={{
              '& .MuiBadge-badge': {
                marginRight: '0px',
                marginTop: '0px',
                right: '-4px',
              },
            }}
          >
            <ChatIcon color="primary" width={18} height={18} />
          </Badge>
        </Box>
      )}
    </Box>
  );
};

NameCell.propTypes = {
  value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  attribute: PropTypes.arrayOf(PropTypes.shape({})),
  allAttributeChats: PropTypes.oneOfType([PropTypes.arrayOf(PropTypes.object)])
    .isRequired,
  handleOpenChat: PropTypes.func.isRequired,
  handleAttributeClick: PropTypes.func.isRequired,
  isTableView: PropTypes.bool,
};

/**
 * Cell renderer for the Name column
 * @param {Object} props - Cell props from React Table
 * @param {any} props.value - The cell value
 * @returns {React.ReactElement} The rendered cell
 */
const NameCellRenderer = (
  props,
  allAttributeChats,
  handleOpenChat,
  handleAttributeClick,
  isTableView,
  mandatoryConstraints
) => {
  const { row, value } = props;

  // Inject is_mandatory from dynamic constraints onto the attribute
  const dynamicConstraint = mandatoryConstraints?.[row?.original?.id];
  const attribute = {
    ...(row?.original || {}),
    ...(dynamicConstraint != null && {
      is_mandatory: dynamicConstraint?.is_mandatory === true,
    }),
  };

  return (
    <NameCell
      value={value}
      attribute={attribute}
      allAttributeChats={allAttributeChats}
      handleOpenChat={handleOpenChat}
      handleAttributeClick={handleAttributeClick}
      isTableView={isTableView}
    />
  );
};

NameCellRenderer.propTypes = {
  value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  row: PropTypes.shape({
    original: PropTypes.shape({
      id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    }),
  }),
  allAttributeChats: PropTypes.oneOfType([PropTypes.arrayOf(PropTypes.object)])
    .isRequired,
  handleOpenChat: PropTypes.func.isRequired,
  handleAttributeClick: PropTypes.func.isRequired,
  isTableView: PropTypes.bool,
};

/**
 * AttributeValueRenderer component for rendering individual attribute values
 * @param {Object} props - Component props
 * @param {Object} props.attr - The attribute data
 * @param {Function} props.renderValueCell - Function to render value cell
 * @returns {React.ReactElement} The rendered attribute value
 */
const AttributeValueRenderer = ({ attr, renderValueCell }) => {
  return (
    <>
      {renderValueCell({
        row: { original: attr },
        value: attr.value,
      })}
    </>
  );
};

AttributeValueRenderer.propTypes = {
  attr: PropTypes.shape({
    id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    value: PropTypes.oneOfType([
      PropTypes.string,
      PropTypes.number,
      PropTypes.bool,
      PropTypes.array,
      PropTypes.object,
    ]),
  }).isRequired,
  renderValueCell: PropTypes.func.isRequired,
};

const AI_HIGHLIGHT_CLASS = 'ai-highlight-attribute';

/**
 * Applies highlight animation to a DOM element and auto-removes it after ~3.5s
 */
const applyHighlightToElement = (element) => {
  if (!element) {
    return;
  }
  element.classList.remove(AI_HIGHLIGHT_CLASS);
  // Force reflow via rAF so re-adding the class restarts the animation
  requestAnimationFrame(() => {
    element.classList.add(AI_HIGHLIGHT_CLASS);
    const cleanup = () => {
      element.classList.remove(AI_HIGHLIGHT_CLASS);
    };
    element.addEventListener('animationend', cleanup, { once: true });
    setTimeout(cleanup, 3600);
  });
};

/**
 * Finds and highlights a table row or card element after scroll
 */
const scrollAndHighlightElement = (container, selector, index) => {
  if (!container) {
    return;
  }
  const elements = container.querySelectorAll(selector);
  const targetEl = elements?.[index];
  if (!targetEl) {
    return;
  }
  targetEl.scrollIntoView({ behavior: 'smooth', block: 'center' });
  setTimeout(() => {
    applyHighlightToElement(targetEl);
  }, 200);
};

const OtherDetails = ({
  sectionsData,
  sectionPermissions,
  attributeValues = EMPTY_OBJECT,
  savedAttributeValues = EMPTY_OBJECT,
  hasDraftChanges = false,
  handleUpdateEntity,
  mode = 'edit',
  entityData = EMPTY_OBJECT,
  isMultipleEditMode = false,
  entityDetailsFormikForm = null,
  teamUsersList = EMPTY_ARRAY,
  isUserAuthorizedForUpdateAttribute,
  allAttributeChats,
  handleOpenChat,
  handleAttributeClick,
  syncAttributes,
  handleGetSectionPermission,
  isBasicDetailsEmpty = true,
  isUserAuthorizedToOverrideAttribute,
  overrideData,
  refreshSingleAttribute,
  handleAutoSave = NOOP,
  isTableView = true,
  getOverrideData,
  getAttributeDetails,
  updateInitialValuesFromAPI,
  allSectionList,
  // Constraint-related props
  lookupSourceAttributes = EMPTY_ARRAY,
  autoCompleteSourceAttributes = EMPTY_ARRAY,
  overrideDropdownOptions = EMPTY_OBJECT,
  isLoadingConstraints = false,
  handleAttributeValueChange = NOOP,
  formDetails = EMPTY_OBJECT,
  isHDFCUAMEnabled = false,
  currentModelStatus = EMPTY_OBJECT,
  dateCalendarConstraints,
  dateCalendarSourceAttributes,
  editableConstraints,
  editableSourceAttributes,
  selectedStateNode,
  entitySelectedGroupId,
  setSelectedStateNode,
  setEntitySelectedGroupId,
  groupDetails = EMPTY_OBJECT,
  isGroupEditModal = false,
  groupListForTeamMembers,
  possibleRoles = EMPTY_ARRAY,
  getNextPossibleStates,
  getModelDetails,
  handleGetAttributesData,
  clearDraftValues,
  mandatoryConstraints = {},
  mandatorySourceAttributes = [],
  getCustomTeamDetails,
  // AI Autofill props
  onAcceptAISuggestion,
  onRejectAISuggestion,
  onUndoAISuggestion,
  getSuggestionForAttribute,
  // AI Attribute Navigation
  targetAttribute,
  associationTypes = EMPTY_OBJECT,
  modelTemplateDetails = EMPTY_OBJECT,
  attributeSourceMapping = EMPTY_OBJECT,
  loading = false,
  getAttributeMapping = NOOP,
}) => {
  const theme = useTheme();
  const prevHiddenIdsRef = React.useRef(new Set());
  const [selectedSection, setSelectedSection] = useState({});
  const [attributes, setAttributes] = useState([]);
  const [openModal, setOpenModal] = useState(false);
  const [isAssignUserModalOpen, setIsAssignUserModalOpen] = useState(false);
  const [selectedAttribute, setSelectedAttribute] = useState(null);
  const [openDerivedModal, setOpenDerivedModal] = useState(false);
  const [skipPageReset, setSkipPageReset] = useState(true);
  const [historyAttribute, setHistoryAttribute] = useState(null);
  const isHistoryDrawerOpen = Boolean(historyAttribute);

  // Store the current values in a ref to track latest changes
  const currentValuesRef = useRef({});
  // Ref to capture table pagination controls from ReactMuiTableListView
  const tableStateRef = useRef({});
  // Ref for the table container DOM element
  const tableContainerRef = useRef(null);
  // Ref to track if the active section has been initialized from URL or defaults on mount
  const isSectionInitializedRef = useRef(false);
  const { pluginConfig } = useSelector((state) => state?.tenant);
  const draftAttributeIds = useDraftAttributeIds();

  const entityId = entityData?.entity_id;
  const entityType = entityData?.entity_type;

  const location = useLocation();
  const [selectedSectionId, setSelectedSectionId] = useStateParams(
    '',
    'section'
  );

  const { data: usersList = EMPTY_ARRAY } = useUsersQuery();

  const handleOpenDerivedModal = useCallback(
    (attribute) => {
      setSelectedAttribute(attribute);
      setOpenDerivedModal(true);
    },
    [setSelectedAttribute, setOpenDerivedModal]
  );
  const handleDerivedModalClose = useCallback(() => {
    setOpenDerivedModal(false);
  }, [setOpenDerivedModal]);

  const handleAssignUserSave = useCallback(() => {
    updateInitialValuesFromAPI();
  }, [updateInitialValuesFromAPI]);

  const refreshSingleAttributeValues = useCallback(
    (attrId, newValue) => {
      // refresh the single attribute
      refreshSingleAttribute(attrId, newValue);
    },
    [refreshSingleAttribute]
  );

  const handleSectionClick = useCallback(
    (section) => {
      if (String(selectedSection?.section_id) === String(section?.section_id)) {
        return;
      }

      setSelectedSection(section);
      setAttributes(section?.attributes || []);
      setSkipPageReset(false);

      const sectionId = section?.section_id;
      if (sectionId !== null && sectionId !== undefined) {
        setSelectedSectionId(
          String(sectionId),
          false,
          location.state ?? null,
          true
        );
      }
    },
    [location.state, selectedSection?.section_id, setSelectedSectionId]
  );

  const sectionHasMandatoryAttribute = useCallback(
    (section) =>
      Boolean(
        section?.attributes?.some((attribute) =>
          checkAttributeIsRequired(attribute)
        )
      ),
    []
  );
  // Initialize active section on mount or when sectionsData first loads
  useEffect(() => {
    if (!sectionsData?.length) {
      setSelectedSection({});
      setAttributes([]);
      return;
    }

    if (isSectionInitializedRef.current) {
      return;
    }

    const matched = selectedSectionId
      ? sectionsData.find(
          (s) => String(s?.section_id) === String(selectedSectionId)
        )
      : null;

    const initialSection = matched || sectionsData[0];

    if (initialSection) {
      setSelectedSection(initialSection);
      setAttributes(initialSection?.attributes || []);

      const initialId = String(initialSection.section_id);
      if (String(selectedSectionId) !== initialId) {
        setSelectedSectionId(initialId, false, location.state ?? null, true);
      }

      isSectionInitializedRef.current = true;
    }
  }, [sectionsData, selectedSectionId, setSelectedSectionId, location.state]);

  const handleEditClick = useCallback(
    (attribute) => {
      setSelectedAttribute(attribute);
      if (attribute?.name === 'assignedUser' && isHDFCUAMEnabled) {
        setIsAssignUserModalOpen(true);
      } else {
        setOpenModal(true);
      }
    },
    [
      setSelectedAttribute,
      setOpenModal,
      setIsAssignUserModalOpen,
      isHDFCUAMEnabled,
    ]
  );

  const handleCloseModal = useCallback(() => {
    setOpenModal(false);
    setIsAssignUserModalOpen(false);
    setSelectedAttribute(null);
  }, [setOpenModal, setIsAssignUserModalOpen, setSelectedAttribute]);

  const handleHistoryClick = useCallback((attribute) => {
    setHistoryAttribute(attribute);
  }, []);

  const handleCloseHistoryDrawer = useCallback(() => {
    setHistoryAttribute(null);
  }, []);

  // Stable handler for form field changes
  const handleFieldChange = useCallback(
    (attributeId, newValue) => {
      const oldValue = entityDetailsFormikForm?.values?.[attributeId];
      setSkipPageReset(true);

      if (entityDetailsFormikForm) {
        // Store the new value in ref immediately
        currentValuesRef.current[attributeId] = newValue;
        entityDetailsFormikForm.setFieldValue(attributeId, newValue);
        if (isMultipleEditMode) {
          handleAutoSave(attributeId, newValue, { persist: false });
        }
      }

      // Handle constraint value changes
      if (handleAttributeValueChange) {
        handleAttributeValueChange(attributeId, newValue, oldValue);
      }
      if (
        isHDFCUAMEnabled &&
        HDFC_HARDCODED_DEPLOYMENT_WORKFLOW_STATES.includes(
          currentModelStatus?.currentState
        )
      ) {
        // Get all attributes from template to find the current attribute
        const allAttributesList = extractAllAttributesFromTemplate(formDetails);
        const currentAttribute = allAttributesList?.find(
          (attr) => attr?.id === attributeId
        );

        // Validate deployment date
        validateDeploymentDate({
          attribute: currentAttribute,
          newValue,
          formDetails,
          attributeValues,
        });

        // Validate deployment date communication delay
        validateDeploymentDateCommunication({
          attribute: currentAttribute,
          newValue,
          formDetails,
          attributeValues,
        });
      }
    },
    [
      entityDetailsFormikForm,
      isMultipleEditMode,
      handleAutoSave,
      handleAttributeValueChange,
      formDetails,
      syncAttributes,
      attributeValues,
      isHDFCUAMEnabled,
      currentModelStatus,
    ]
  );

  const handleClearSelectedState = useCallback(() => {
    setSelectedStateNode({});
    setEntitySelectedGroupId('');
    handleGetSectionPermission({
      entityType: entityType,
      entityId: entityId,
    });
  }, [
    setSelectedStateNode,
    setEntitySelectedGroupId,
    entityId,
    entityType,
    handleGetSectionPermission,
  ]);

  // Handler for saving attribute values
  const handleSave = useCallback(
    (attribute, newValue) => {
      handleUpdateEntity(
        { [attribute?.id]: newValue },
        attribute?.attribute_origin
      ).then((res) => {
        if (res) {
          // Always update Formik with new value for consistency
          if (entityDetailsFormikForm) {
            entityDetailsFormikForm.setFieldValue(attribute?.id, newValue);
          }

          // If syncAttributes available, merge into form values
          if (syncAttributes && entityDetailsFormikForm) {
            entityDetailsFormikForm.setValues((prev) => ({
              ...prev,
              ...syncAttributes,
            }));
          }
          handleGetSectionPermission({ entityId, entityType });
          getAttributeMapping();
          setSkipPageReset(true);
        }
      });
    },
    [
      handleUpdateEntity,
      syncAttributes,
      entityDetailsFormikForm,
      entityId,
      entityType,
      handleGetSectionPermission,
      getAttributeMapping,
      formDetails,
      attributeValues,
    ]
  );

  // Compute allFormValues for conditional evaluation
  const allFormValues = useMemo(() => {
    const baseValues = { ...attributeValues, ...syncAttributes };
    // Add formik values if available
    if (entityDetailsFormikForm?.values) {
      Object.assign(baseValues, entityDetailsFormikForm.values);
    }
    return baseValues;
  }, [attributeValues, syncAttributes, entityDetailsFormikForm?.values]);

  // Transform attributes array to include values from attributeValues and apply conditional visibility
  const { selectedSecVisibleAtt: attributesWithValues, hiddenAttributes } =
    useMemo(() => {
      const selectedSectionAttributes =
        attributes?.map((attr) => ({
          ...attr,
          // set `value` for each attribute:
          // 1. Prefer value from syncAttributes if available
          // 2. Else, take from attributeValues (initial values passed as props)
          // 3. If neither exists, default to an empty string ''
          value:
            syncAttributes?.[attr?.id] ?? attributeValues?.[attr?.id] ?? '',
        })) || [];

      /**
       * on initial render, `allFormValues` will be empty.
       * it is guaranteed that `allFormValues` is never an empty object after initialization,
       * so return early when it is empty.
       */
      if (isValueEmpty(allFormValues)) {
        return {
          selectedSecVisibleAtt: withDraftRowFlag(
            selectedSectionAttributes,
            draftAttributeIds
          ),
          hiddenAttributes: [],
        };
      }

      // Apply conditional visibility filtering
      const allSectionAttributes = sectionsData?.flatMap(
        (section) => section?.attributes || []
      );
      const { visibleAttributes: selectedSecVisibleAtt } = getVisibleAttributes(
        selectedSectionAttributes,
        allFormValues
      );
      const { hiddenAttributes: allHiddenAttributes } = getVisibleAttributes(
        allSectionAttributes,
        allFormValues
      );

      return {
        selectedSecVisibleAtt: withDraftRowFlag(
          selectedSecVisibleAtt,
          draftAttributeIds
        ),
        hiddenAttributes: allHiddenAttributes,
      };
    }, [
      attributes,
      attributeValues,
      syncAttributes,
      allFormValues,
      sectionsData,
      draftAttributeIds,
    ]);

  // Re-enable skipPageReset after data changes so table edits don't reset the page.
  useEffect(() => {
    setSkipPageReset(true);
  }, [attributesWithValues, setSkipPageReset]);

  /**
   * we need this useEffect to reset the values of hidden attributes
   * whenever hidden attributes changes and are different from previous hidden attributes
   * we will reset the values of hidden attributes to their initial values
   */
  useEffect(() => {
    /**
     * on initial render we will be getting empty initialValues inside entityDetailsFormikForm
     */
    if (
      !entityDetailsFormikForm?.setFieldValue ||
      !entityDetailsFormikForm?.initialValues ||
      Object.keys(entityDetailsFormikForm?.initialValues).length === 0
    ) {
      return;
    }

    const currentHiddenIds = new Set(hiddenAttributes.map((a) => a.id));
    const prevHiddenIds = prevHiddenIdsRef.current;

    // if hidden sets are equal, do nothing
    // need this to prevent infinite loop
    if (areSetsEqual(prevHiddenIds, currentHiddenIds)) return;

    const newInitialValues = createInitialFormValues(hiddenAttributes);

    // if hidden att set changed -> reset the values of hidden attributes to initial values
    hiddenAttributes.forEach((attr) => {
      const newValue = newInitialValues[attr.id] || null;
      entityDetailsFormikForm.setFieldValue(attr.id, newValue);
    });

    // if code flow reaches here, means hidden sets have changed, update the ref to latest hidden att ids
    prevHiddenIdsRef.current = currentHiddenIds;
  }, [hiddenAttributes, entityDetailsFormikForm]);

  // Capture table pagination state from ReactMuiTableListView
  const handleTableStateChange = useCallback((state) => {
    tableStateRef.current = state;
  }, []);

  // Handle targeted attribute navigation from AI Drawer
  useEffect(() => {
    if (!targetAttribute?.attrId) {
      return;
    }

    const { attrId, sectionName } = targetAttribute;

    // Find the first matching section containing this attribute
    let targetSection = null;
    if (sectionName) {
      targetSection = sectionsData?.find((section) => {
        const nameMatch =
          section?.section_name === sectionName ||
          section?.name === sectionName;
        const hasAttr = section?.attributes?.some(
          (attr) => String(attr?.id) === String(attrId)
        );
        return nameMatch && hasAttr;
      });
    }
    // Fallback: find first section containing the attribute
    if (!targetSection) {
      targetSection = sectionsData?.find((section) =>
        section?.attributes?.some((attr) => String(attr?.id) === String(attrId))
      );
    }

    if (!targetSection) {
      return;
    }

    // If we need to switch sections, do it
    const needsSectionSwitch =
      selectedSection?.section_id !== targetSection?.section_id;

    if (needsSectionSwitch) {
      setSelectedSection(targetSection);
      setAttributes(targetSection?.attributes);
      setSkipPageReset(false);
    }

    // Calculate timings — need extra delay if section switched for re-render
    const sectionSwitchDelay = needsSectionSwitch ? 350 : 50;

    setTimeout(() => {
      // Get fresh visible attributes after potential section switch
      const sectionAttrs =
        targetSection?.attributes?.map((attr) => ({
          ...attr,
          value:
            syncAttributes?.[attr?.id] ?? attributeValues?.[attr?.id] ?? '',
        })) || [];

      // Find index of the target attribute in the visible list
      const attrIndex = sectionAttrs.findIndex(
        (attr) => String(attr?.id) === String(attrId)
      );

      if (attrIndex < 0) {
        return;
      }

      if (isTableView) {
        // Table view: navigate to correct page, then highlight row
        const pageSize = tableStateRef.current?.pageSize || 25;
        const targetPage = Math.floor(attrIndex / pageSize);
        const rowIndexInPage = attrIndex % pageSize;

        if (tableStateRef.current?.gotoPage) {
          tableStateRef.current.gotoPage(targetPage);
        }

        // Wait for page render, then find and highlight the row
        setTimeout(() => {
          scrollAndHighlightElement(
            tableContainerRef?.current,
            'tbody tr',
            rowIndexInPage
          );
        }, 250);
      } else {
        // Card view: find card by index
        setTimeout(() => {
          scrollAndHighlightElement(
            tableContainerRef?.current,
            '.MuiCard-root',
            attrIndex
          );
        }, 100);
      }
    }, sectionSwitchDelay);
  }, [targetAttribute]);

  const selectedSectionPermission = useMemo(() => {
    // When opened from a group edit modal, we want to allow 'manage' permissions by default
    // to ensure all attributes can be edited in the modal.
    if (isGroupEditModal) return 'manage';
    //return section permission received from BE, else Manage as default
    return (
      sectionPermissions?.section_permissions?.[selectedSection?.section_id] ||
      'manage'
    );
  }, [selectedSection, sectionPermissions, isGroupEditModal]);
  const derivedMapping = useMemo(() => {
    const map = {};
    attributesWithValues?.forEach((attr) => {
      map[attr.id] = Boolean(attr?.is_derived);
    });
    return map;
  }, [attributesWithValues]);

  // Create a ref to store the current context to avoid dependency issues
  const cellContextRef = useRef({});

  // Update the ref with current values
  cellContextRef.current = {
    handleEditClick,
    selectedSectionPermission,
    mode,
    isMultipleEditMode,
    entityDetailsFormikForm,
    usersList,
    teamUsersList,
    entityData,
    handleFieldChange,
    isUserAuthorizedForUpdateAttribute,
    attributeSourceMapping,
    loading,
    isTableView,
    derivedMapping,
    isUserAuthorizedToOverrideAttribute,
    overrideData,
    handleOpenDerivedModal,
    handleAutoSave,
    hasDraftChanges,
    // Constraint-related props
    lookupSourceAttributes,
    autoCompleteSourceAttributes,
    overrideDropdownOptions,
    isLoadingConstraints,
    dateCalendarConstraints,
    dateCalendarSourceAttributes,
    editableConstraints,
    editableSourceAttributes,
    mandatoryConstraints,
    mandatorySourceAttributes,
    groupDetails,
    attributeValues,
    savedAttributeValues,
    onHistoryClick: handleHistoryClick,
    associationTypes,
    // AI Autofill props
    getSuggestionForAttribute,
    onAcceptAISuggestion,
    onRejectAISuggestion,
    onUndoAISuggestion,
    modelTemplateDetails,
    // Name Cell dependencies
    allAttributeChats,
    handleOpenChat,
    handleAttributeClick,
  };

  // Stable cell renderer that uses ref to access current context
  const renderValueCell = useCallback((props) => {
    const context = cellContextRef.current;
    const editPermission =
      context?.selectedSectionPermission === 'manage' &&
      context?.mode === 'edit' &&
      context?.isUserAuthorizedForUpdateAttribute;
    // eslint-disable-next-line react/prop-types
    const attributeId = props?.row?.original?.id;
    const isDerived = context?.derivedMapping[attributeId] === true;

    return ValueCellRenderer(
      props,
      context?.handleEditClick,
      editPermission,
      context?.isMultipleEditMode,
      context?.entityDetailsFormikForm,
      context?.usersList,
      context?.teamUsersList,
      context?.entityData,
      context?.handleFieldChange,
      context?.attributeSourceMapping,
      context?.loading,
      context?.isTableView,
      isDerived,
      context?.isUserAuthorizedToOverrideAttribute,
      context?.overrideData,
      context?.handleOpenDerivedModal,
      context?.handleAutoSave,
      // Constraint-related props
      context?.lookupSourceAttributes,
      context?.autoCompleteSourceAttributes,
      context?.overrideDropdownOptions,
      context?.isLoadingConstraints,
      context?.dateCalendarConstraints,
      context?.dateCalendarSourceAttributes,
      context?.editableConstraints,
      context?.editableSourceAttributes,
      context?.mandatoryConstraints,
      context?.mandatorySourceAttributes,
      context?.hasDraftChanges,
      context?.groupDetails,
      context?.attributeValues,
      context?.savedAttributeValues,
      context?.associationTypes,
      // AI Autofill props
      context?.getSuggestionForAttribute,
      context?.onAcceptAISuggestion,
      context?.onRejectAISuggestion,
      context?.onUndoAISuggestion,
      context?.onHistoryClick,
      context?.modelTemplateDetails
    );
  }, []);

  // Memoize the Name Cell renderer function
  const renderNameCell = useCallback((props) => {
    const context = cellContextRef.current;
    return NameCellRenderer(
      props,
      context?.allAttributeChats,
      context?.handleOpenChat,
      context?.handleAttributeClick,
      context?.isTableView,
      context?.mandatoryConstraints
    );
  }, []);

  const columns = useMemo(
    () => [
      {
        Header: 'Name',
        accessor: 'label',
        Cell: renderNameCell,
      },
      {
        Header: 'Value',
        accessor: 'value',
        Cell: renderValueCell,
      },
    ],
    [renderNameCell, renderValueCell]
  );

  // Create pre-bound click handlers for section items
  const createSectionClickHandler = useCallback(
    (section) => () => {
      handleSectionClick(section);
    },
    [handleSectionClick]
  );

  const renderSectionItem = useCallback(
    (section) => {
      // Pre-bind the click handler for this specific section
      const onClickHandler = createSectionClickHandler(section);

      return (
        <ListItemButton
          key={section?.section_id}
          selected={selectedSection?.section_id === section?.section_id}
          onClick={onClickHandler}
          sx={{
            paddingRight: 1,
            paddingLeft: 2,
            width: '100%',
            overflow: 'hidden',
          }}
        >
          <SectionListItemText
            text={section?.section_name}
            sectionId={section?.section_id}
            hasMandatoryAttribute={sectionHasMandatoryAttribute(section)}
            attributes={section?.attributes}
          />
        </ListItemButton>
      );
    },
    [
      selectedSection?.section_id,
      createSectionClickHandler,
      SectionListItemText,
      sectionHasMandatoryAttribute,
    ]
  );

  const handleGetCellProps = useCallback(
    (cell) => {
      const isDraftRow = cell?.row?.original?.isDraftRow;
      const isNameColumn = cell?.column?.id === 'label';
      // See '.chat-badge-cell' in Theme/base.js: opens up the cell's
      // inner wrapper so the chat-note badge on the Name column
      // isn't clipped. Keep 'list-table-cell' too - this className
      // replaces (rather than merges with) the one already set on
      // the TableCell.
      const chatBadgeCellProps = isNameColumn
        ? { className: 'list-table-cell chat-badge-cell' }
        : {};

      if (isDraftRow) {
        return {
          ...chatBadgeCellProps,
          style: {
            height: 'auto',
            overflow: 'visible',
            whiteSpace: 'normal',
            backgroundColor: theme.palette.draft.rowBg,
            verticalAlign: 'top',
          },
        };
      }

      if (isMultipleEditMode) {
        return {
          ...chatBadgeCellProps,
          style: {
            height: 'auto',
            overflow: 'visible',
            verticalAlign: 'middle',
          },
        };
      }

      return {
        ...chatBadgeCellProps,
        style: {
          height: STANDARD_ROW_HEIGHT,
          ...(isNameColumn ? { overflow: 'visible' } : {}),
        },
      };
    },
    [isMultipleEditMode]
  );

  const handleGetRowProps = useCallback((row) => {
    if (!row?.original?.isDraftRow) {
      return {};
    }

    return {
      'data-draft-row': 'true',
    };
  }, []);

  const descriptionBlock = useMemo(() => {
    const desc = selectedSection?.description;
    if (isRichContentEmpty(desc)) return null;

    return (
      <Alert
        key={selectedSection?.section_id || 'section-description'}
        severity="info"
        sx={{
          width: '100%',
          alignItems: 'flex-start',
          '& .MuiAlert-icon': {
            alignItems: 'flex-start',
            mt: '14px',
          },
          '& .MuiAlert-message': {
            width: '100%',
          },
        }}
        icon={<InfoOutlinedIcon width={18} height={18} />}
      >
        <TextEditor content={desc} readOnly collapsible maxLines={1} />
      </Alert>
    );
  }, [selectedSection?.section_id, selectedSection?.description]);

  return (
    <Grid container spacing={2}>
      {!isGroupEditModal && !isBasicDetailsEmpty && (
        <Grid
          size={12}
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
          }}
        >
          <Box>
            {!isValueEmpty(selectedStateNode) && (
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 2,
                }}
              >
                <Typography variant="body2">
                  {`Viewing State "${selectedStateNode}" Data.`}
                </Typography>{' '}
                <Button
                  onClick={handleClearSelectedState}
                  variant="outlined"
                  size="small"
                >
                  close
                </Button>
              </Box>
            )}{' '}
            {!isGroupEditModal &&
              !isValueEmpty(entitySelectedGroupId?.groupName) && (
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 2,
                  }}
                >
                  <Typography variant="body2">
                    {`Viewing Group "${entitySelectedGroupId?.groupName}" Data.`}
                  </Typography>{' '}
                  <Button
                    onClick={handleClearSelectedState}
                    variant="outlined"
                    size="small"
                  >
                    close
                  </Button>
                </Box>
              )}
          </Box>
        </Grid>
      )}
      <Grid size={3}>
        <Card sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
          <List
            component="nav"
            sx={{
              p: 0,
              width: '100%',
              overflow: 'hidden',
            }}
          >
            {sectionsData?.map(renderSectionItem)}
          </List>
        </Card>
      </Grid>
      {isTableView && (
        <Grid size={9}>
          {descriptionBlock}
          <Card
            ref={tableContainerRef}
            sx={{
              mt: 1,
              height: '100%',
              display: 'flex',
              flexDirection: 'column',
              '& .MuiTablePagination-root .MuiTablePagination-toolbar': {
                background: 'transparent !important',
              },
              '& tr[data-draft-row="true"] .list-table-cell': {
                padding: `${DRAFT_ROW_CELL_PADDING} !important`,
                height: 'auto !important',
                verticalAlign: 'top',
              },
            }}
          >
            <ReactMuiTableListView
              isDeletebyId={true}
              data={attributesWithValues}
              columns={columns}
              skipPageReset={skipPageReset}
              getHeaderProps={handleGetHeaderProps}
              getRowProps={handleGetRowProps}
              enableRowSelection={true}
              pageCount={attributesWithValues?.length}
              enablePagination={true}
              isAllOptionDisabled={true}
              initialPageSize={25}
              rowsPerPageOptions={[25, 50, 100]}
              stickyHeader={true}
              dataLength={attributesWithValues?.length || 0}
              getCellProps={handleGetCellProps}
              noSearchResultMessage={
                pluginConfig?.HDFCUAM ? 'Not applicable.' : 'No data found.'
              }
              onTableStateChange={handleTableStateChange}
            />
          </Card>
        </Grid>
      )}
      {!isTableView && (
        <Grid
          container
          rowSpacing={1}
          size={9}
          sx={{ alignContent: 'flex-start', alignItems: 'flex-start' }}
        >
          {descriptionBlock}
          {(attributesWithValues?.length ?? 0) > 0 ? (
            <Grid size={12}>
              <Box
                ref={tableContainerRef}
                sx={{
                  height: '100%',
                  display: 'flex',
                  flexDirection: 'column',
                  p: 0,
                  pb: 2,
                }}
              >
                {attributesWithValues?.map((attr) => (
                  <Card
                    key={attr?.id}
                    sx={{
                      p: 1,
                      display: 'flex',
                      flexDirection: 'column',
                      gap: 0.5,
                      ...(attr?.isDraftRow
                        ? { bgcolor: theme.palette.draft.rowBg }
                        : {}),
                    }}
                  >
                    <Box>
                      {renderNameCell({
                        row: { original: attr },
                        value: attr.label || attr.name,
                      })}
                    </Box>
                    <AttributeValueRenderer
                      attr={attr}
                      renderValueCell={renderValueCell}
                    />
                  </Card>
                ))}
              </Box>
            </Grid>
          ) : (
            <Box
              sx={{
                display: 'flex',
                flexGrow: 1,
                alignItems: 'center',
                flexDirection: 'column',
                justifyContent: 'center',
                gap: 1,
                height: 'calc(100vh  - 310px)',
              }}
            >
              <NoDataPresentComponent
                message={
                  pluginConfig?.HDFCUAM ? 'Not applicable.' : 'No data found.'
                }
              />
            </Box>
          )}
        </Grid>
      )}
      {selectedAttribute && (
        <EditValueModal
          open={openModal}
          handleClose={handleCloseModal}
          attribute={selectedAttribute}
          value={attributeValues[selectedAttribute?.id] || ''}
          handleSave={handleSave}
          usersList={usersList}
          entityData={entityData}
          onCalculateAttribute={handleOpenDerivedModal}
          formDetails={formDetails}
          attributeValues={attributeValues}
          isHDFCUAMEnabled={isHDFCUAMEnabled}
          currentModelStatus={currentModelStatus}
          groupDetails={groupDetails}
          modelTemplateDetails={modelTemplateDetails}
        />
      )}
      {isAssignUserModalOpen && (
        <AssignUserModal
          open={isAssignUserModalOpen}
          handleClose={handleCloseModal}
          attribute={selectedAttribute}
          entityData={entityData}
          usersList={usersList}
          groupList={groupListForTeamMembers}
          value={attributeValues[selectedAttribute?.id] || ''}
          onSave={handleAssignUserSave}
          possibleRoles={possibleRoles}
          teamUsersList={teamUsersList}
          getNextPossibleStates={getNextPossibleStates}
          getModelDetails={getModelDetails}
          handleGetAttributesData={handleGetAttributesData}
          clearDraftValues={clearDraftValues}
          getCustomTeamDetails={getCustomTeamDetails}
        />
      )}
      {openDerivedModal && (
        <DerivedAttributeOverrideModal
          open={openDerivedModal}
          handleClose={handleDerivedModalClose}
          attribute={selectedAttribute}
          entityDetailsFormikForm={entityDetailsFormikForm}
          teamUsersList={teamUsersList}
          allAttributeChats={allAttributeChats}
          entityData={entityData}
          isUserAuthorizedToOverrideAttribute={
            isUserAuthorizedToOverrideAttribute
          }
          overrideData={overrideData}
          refreshSingleAttribute={refreshSingleAttributeValues}
          getOverrideData={getOverrideData}
          getAttributeDetails={getAttributeDetails}
          updateInitialValuesFromAPI={updateInitialValuesFromAPI}
          handleAutoSave={handleAutoSave}
          getAttributeMapping={getAttributeMapping}
          allSectionList={allSectionList}
          handleAttributeValueChange={handleAttributeValueChange}
        />
      )}
      {isHistoryDrawerOpen && (
        <HistoryDrawer
          open={isHistoryDrawerOpen}
          handleClose={handleCloseHistoryDrawer}
          attribute={historyAttribute}
          entityId={entityData?.entity_id}
          entityType={entityData?.entity_type}
        />
      )}
    </Grid>
  );
};

OtherDetails.propTypes = {
  sectionsData: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
      name: PropTypes.string,
      section_name: PropTypes.string,
      attributes: PropTypes.arrayOf(
        PropTypes.shape({
          id: PropTypes.oneOfType([PropTypes.string, PropTypes.number])
            .isRequired,
          name: PropTypes.string,
          value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
          dataType: PropTypes.string,
        })
      ),
    })
  ).isRequired,
  hasDraftChanges: PropTypes.bool,
  groupDetails: PropTypes.shape({
    isGroupAdmin: PropTypes.bool,
    userName: PropTypes.string,
    userId: PropTypes.number,
    users: PropTypes.arrayOf(
      PropTypes.shape({
        userId: PropTypes.number,
        username: PropTypes.string,
      })
    ),
  }),
  sectionPermissions: PropTypes.oneOfType([PropTypes.object]).isRequired,
  attributeValues: PropTypes.objectOf(
    PropTypes.oneOfType([
      PropTypes.string,
      PropTypes.number,
      PropTypes.bool,
      PropTypes.array,
      PropTypes.object,
    ])
  ),
  savedAttributeValues: PropTypes.objectOf(
    PropTypes.oneOfType([
      PropTypes.string,
      PropTypes.number,
      PropTypes.bool,
      PropTypes.array,
      PropTypes.object,
    ])
  ),
  handleUpdateEntity: PropTypes.func.isRequired,
  mode: PropTypes.oneOf(['edit', 'view']),
  entityData: PropTypes.shape({
    entity_id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    entity_type: PropTypes.string,
    request_type: PropTypes.string,
  }),
  isMultipleEditMode: PropTypes.bool,
  isTableView: PropTypes.bool,
  entityDetailsFormikForm: PropTypes.shape({
    values: PropTypes.objectOf(
      PropTypes.oneOfType([
        PropTypes.string,
        PropTypes.number,
        PropTypes.bool,
        PropTypes.array,
        PropTypes.object,
      ])
    ),
    setFieldValue: PropTypes.func,
    setFieldTouched: PropTypes.func,
    errors: PropTypes.objectOf(PropTypes.string),
    touched: PropTypes.objectOf(PropTypes.bool),
    initialValues: PropTypes.shape({}),
  }),
  teamUsersList: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
      name: PropTypes.string,
      display_name: PropTypes.string,
    })
  ),
  isUserAuthorizedForUpdateAttribute: PropTypes.bool.isRequired,
  allAttributeChats: PropTypes.oneOfType([PropTypes.arrayOf(PropTypes.object)])
    .isRequired,
  handleOpenChat: PropTypes.func.isRequired,
  handleAttributeClick: PropTypes.func.isRequired,
  handleGetSectionPermission: PropTypes.func.isRequired,
  syncAttributes: PropTypes.oneOfType([PropTypes.object]),
  isBasicDetailsEmpty: PropTypes.bool.isRequired,
  overrideData: PropTypes.oneOf([PropTypes.object]).isRequired,
  isUserAuthorizedToOverrideAttribute: PropTypes.bool.isRequired,
  refreshSingleAttribute: PropTypes.func.isRequired,
  handleAutoSave: PropTypes.func.isRequired,
  getOverrideData: PropTypes.func.isRequired,
  getAttributeDetails: PropTypes.func,
  updateInitialValuesFromAPI: PropTypes.func.isRequired,
  allSectionList: PropTypes.oneOf([PropTypes.object]).isRequired,
  // Constraint-related props
  lookupSourceAttributes: PropTypes.arrayOf(PropTypes.shape({})),
  autoCompleteSourceAttributes: PropTypes.arrayOf(PropTypes.shape({})),
  overrideDropdownOptions: PropTypes.objectOf(
    PropTypes.oneOfType([PropTypes.string, PropTypes.object])
  ),
  isLoadingConstraints: PropTypes.bool,
  handleAttributeValueChange: PropTypes.func,
  formDetails: PropTypes.shape({}),
  isHDFCUAMEnabled: PropTypes.bool,
  currentModelStatus: PropTypes.shape({
    currentState: PropTypes.string,
  }),
  dateCalendarConstraints: PropTypes.shape({}),
  dateCalendarSourceAttributes: PropTypes.arrayOf(PropTypes.shape({})),
  editableConstraints: PropTypes.shape({}),
  editableSourceAttributes: PropTypes.arrayOf(PropTypes.shape({})),
  selectedStateNode: PropTypes.string,
  entitySelectedGroupId: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.number,
  ]),
  setSelectedStateNode: PropTypes.func,
  setEntitySelectedGroupId: PropTypes.func,
  isGroupEditModal: PropTypes.bool,
  possibleRoles: PropTypes.arrayOf(PropTypes.string),
  groupListForTeamMembers: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
      name: PropTypes.string,
      display_name: PropTypes.string,
    })
  ),
  getNextPossibleStates: PropTypes.func,
  getModelDetails: PropTypes.func,
  handleGetAttributesData: PropTypes.func,
  mandatoryConstraints: PropTypes.shape({}),
  mandatorySourceAttributes: PropTypes.arrayOf(PropTypes.number),
  clearDraftValues: PropTypes.func,
  getCustomTeamDetails: PropTypes.func,
  // AI Autofill
  onAcceptAISuggestion: PropTypes.func,
  onRejectAISuggestion: PropTypes.func,
  onUndoAISuggestion: PropTypes.func,
  getSuggestionForAttribute: PropTypes.func,
  // AI Attribute Navigation
  targetAttribute: PropTypes.shape({
    attrId: PropTypes.string,
    sectionName: PropTypes.string,
    timestamp: PropTypes.number,
  }),
  associationTypes: PropTypes.objectOf(
    PropTypes.arrayOf(
      PropTypes.shape({
        entity_name: PropTypes.string,
        entity_type: PropTypes.string,
        template_id: PropTypes.string,
        version_id: PropTypes.string,
      })
    )
  ),
  modelTemplateDetails: PropTypes.shape({}),
  attributeSourceMapping: PropTypes.objectOf(PropTypes.shape({})),
  loading: PropTypes.bool,
  getAttributeMapping: PropTypes.func,
};

export default OtherDetails;
